import { Icon } from '@iconify/react'
import {
  Button,
  Chip,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from '@nextui-org/react'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useSocketContextData } from 'src/context/socket'
import { useSubscriptionContext } from 'src/context/subscription'
import { useWorkspaceContextData } from 'src/context/workspace'
import { TipTapEditor } from 'src/pages/powerdoc-view/editor/tiptap'
import { PowerDocsEventsName } from 'src/types/SocketEvents'
const variants = ['Proposal', 'Quotation', 'Offer']

export default function TenderDocuments({ tender, getTender }: any) {
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const { activeWorkspace } = useWorkspaceContextData()
  const { io } = useSocketContextData()
  const { profile } = useWorkspaceContextData()
  const { getUserSubscription, currentLimitations } = useSubscriptionContext()
  const {
    isOpen: isSubscriptionModalOpen,
    onOpen: openSubscriptionModal,
    onClose: closeSubscriptionModal,
  } = useDisclosure()

  const generateSubDoc = async (variant: string, subType: string) => {
    if (currentLimitations) {
      if (currentLimitations.isFreeTrial || (currentLimitations?.hasSubscription && currentLimitations.currentSubscription === 'EXECUTE')) {
        if (currentLimitations.currentSubscription === 'EXECUTE') {      
          toast.loading('Generating ' + variant.toLowerCase() + '...')
          setIsGenerating(true)
          console.log(tender)
          io.emitWithAck(PowerDocsEventsName.GENERATE_SUBDOCUMENT, {
            token: localStorage.getItem('accessToken'),
            workspaceUUID: activeWorkspace,
            powerDocUUID: tender.powerdoc.uuid,
            variant,
            subType,
          })
        }else{
          openSubscriptionModal()
        }
      } else {
        openSubscriptionModal()
      }
    }
  }
  const documentsTypes = useMemo(
    () => [
      {
        title: 'Sales Proposal',
        description: 'Convince a client to purchase a product or service.',
        documentType: 'PROPOSAL',
        subType: 'Sales Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Tender Proposal',
        description: 'Bid for public contracts or services by responding to an RFP.',
        documentType: 'PROPOSAL',
        subType: 'Tender Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Project Proposal',
        description: 'Outline a plan to execute a specific project for approval.',
        documentType: 'PROPOSAL',
        subType: 'Project Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Offer',
        description: 'A formal proposal outlining the services, terms, & pricing, which becomes binding once accepted.',
        documentType: 'OFFER',
        subType: '',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Quotation',
        description: 'A formal statement outlining the price, terms, and conditions of a proposed offer or contract.',
        documentType: 'QUOTATION',
        subType: '',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Contract',
        description: 'Legally binding document detailing obligations, responsibilities, and terms.',
        documentType: 'CONTRACT',
        subType: '',
        available: false,
        icon: 'lucide:file-text',
      },
      {
        title: 'NDA (Non-Disclosure Agreement)',
        description: 'A Contract protecting confidential information.',
        documentType: 'NDA',
        subType: '',
        available: false,
        icon: 'lucide:file-text',
      },
      {
        title: 'Invoice',
        description: 'A request for payment after work is completed.',
        documentType: 'INVOICE',
        subType: '',
        available: false,
        icon: 'lucide:file-text',
      },
    ],
    [],
  )
  const [drawerConfig, setDrawerConfig] = useState<any>({ isOpen: false, variant: 'proposal' })
  const [docDesignSettings, setDocDesignSettings] = useState<any>({})
  useEffect(() => {
    window.variant = drawerConfig.variant
    setDocDesignSettings(
      tender?.powerdoc?.[drawerConfig.variant?.toLowerCase()]?.designSetting ?? {
        fontType: 'Inter',
        fontSize: 2, // 1 = small, 2 = medium, 3 = large
        sectionSpacing: 4, // between 1-7
      },
    )
  }, [drawerConfig])
  useEffect(() => {
    if (io) {
      io.on(PowerDocsEventsName.QUOTATION_DONE, async () => {
        setIsGenerating(false)
        await getTender()
        setDrawerConfig({
          isOpen: true,
          variant: 'quotation',
        })
        toast.dismiss()
      })
      io.on(PowerDocsEventsName.OFFER_DONE, async () => {
        setIsGenerating(false)
        await getTender()
        setDrawerConfig({
          isOpen: true,
          variant: 'offer',
        })
        toast.dismiss()
      })
      io.on(PowerDocsEventsName.PROPOSAL_DONE, async () => {
        setIsGenerating(false)
        await getTender()
        setDrawerConfig({
          isOpen: true,
          variant: 'proposal',
        })
        toast.dismiss()
      })
      io.on(PowerDocsEventsName.CUSTOM_DOME, async ({ id }: { id: number }) => {
        setIsGenerating(false)
        await getTender()
        const index = tender?.powerdoc?.customDocs?.findIndex((item: any) => item.id === id) ?? 0
        setDrawerConfig({
          isOpen: true,
          variant: 'CUSTOM',
          index,
        })
        toast.dismiss()
      })
    }
    if (io && tender.powerdoc?.uuid) {
      io.emit('join_channel', { channelName: tender.pwoerdocs?.uuid })
    }
  }, [io, tender])

  const isEmpty =
    tender?.powerdoc?.customDocs?.length === 0 && variants.map((variant) => tender.powerdoc?.[variant.toLowerCase()]).filter((item) => item).length === 0
  return (
    <div className="pt-4">
      <Table
        classNames={{
          wrapper: 'p-0 shadow-none',
        }}
      >
        <TableHeader>
          <TableColumn className={'text-black bg-[#C3E3E8]'} width={'30%'}>
            Document
          </TableColumn>
          <TableColumn className={'text-black bg-[#C3E3E8]'} width={'10%'}>
            Status
          </TableColumn>
          <TableColumn className={'text-black bg-[#C3E3E8]'} width={'10%'}>
            Actions
          </TableColumn>
        </TableHeader>
        <TableBody>
          {tender?.powerdoc?.proposal && (
            <TableRow>
              <TableCell>
                <span className="flex items-center gap-1 font-semibold">
                  <Icon icon="lucide:file-text" width="18" /> Proposal
                </span>
              </TableCell>
              <TableCell>
                <span>{tender?.powerdoc?.proposal?.status}</span>
              </TableCell>
              <TableCell>
                <Button
                  variant="bordered"
                  size="md"
                  onPress={() => {
                    setDrawerConfig({ isOpen: true, variant: 'proposal' })
                  }}
                >
                  <Icon icon="lucide:file-pen" width="18" className="text-secondary" />
                  Open
                </Button>
              </TableCell>
            </TableRow>
          )}

          {tender?.powerdoc?.quotation && (
            <TableRow>
              <TableCell>
                <span className="flex items-center gap-1 font-semibold">
                  <Icon icon="lucide:file-check" width="18" /> Quotation
                </span>
              </TableCell>
              <TableCell>
                <span>{tender?.powerdoc?.quotation?.status}</span>
              </TableCell>
              <TableCell>
                <Button
                  variant="bordered"
                  size="md"
                  onPress={() => {
                    setDrawerConfig({ isOpen: true, variant: 'proposal' })
                  }}
                >
                  <Icon icon="lucide:file-pen" width="18" className="text-secondary" />
                  Open
                </Button>
              </TableCell>
            </TableRow>
          )}

          {tender?.powerdoc?.offer && (
            <TableRow>
              <TableCell>
                <span className="flex items-center gap-1 font-semibold">
                  <Icon icon="lucide:file-spreadsheet" width="18" /> Offer
                </span>
              </TableCell>
              <TableCell>
                <span>{tender?.powerdoc?.offer?.status}</span>
              </TableCell>
              <TableCell>
                <Button
                  variant="bordered"
                  size="md"
                  onPress={() => {
                    setDrawerConfig({ isOpen: true, variant: 'proposal' })
                  }}
                >
                  <Icon icon="lucide:file-pen" width="18" className="text-secondary" />
                  Open
                </Button>
              </TableCell>
            </TableRow>
          )}

          {tender?.powerdoc?.customDocs?.map((customDoc: any, index: number) => (
            <>
              <TableRow>
                <TableCell>
                  <span className="flex items-center gap-1 font-semibold">
                    <Icon icon="lucide:file-spreadsheet" width="18" /> {customDoc.documentType}
                  </span>
                </TableCell>
                <TableCell>
                  <span>{customDoc?.status}</span>
                </TableCell>
                <TableCell>
                  <Button
                    variant="bordered"
                    size="md"
                    onPress={() => {
                      setDrawerConfig({ isOpen: true, variant: 'CUSTOM', index: index })
                    }}
                  >
                    <Icon icon="lucide:file-pen" width="18" className="text-secondary" />
                    Open
                  </Button>
                </TableCell>
              </TableRow>
            </>
          ))}
        </TableBody>
      </Table>
      {isEmpty && (
        <div className="p-4 flex flex-col items-center mt-2">
          {profile.serviceTier === 'SELF_SERVE' ? (
            <>
              <Icon icon="lucide:file-question" fontSize={48} className="text-default-400" />
              <span className="text-xl font-medium text-default-400">No document is created yet</span>
              <span className="text-xl font-medium text-default-400">
                Start by creating the right document for your project
              </span>
              <div className="flex flex-col gap-8 p-2 mt-2">
                <div className="grid grid-cols-4 gap-4">
                  {documentsTypes.map((documentOption) => (
                    <div
                      key={documentOption.title}
                      className={`flex flex-col gap-3 border-3 p-4 py-8 rounded-lg ${
                        documentOption.available && !isGenerating ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                      } border-default-200`}
                      onClick={() =>
                        documentOption.available && !isGenerating
                          ? generateSubDoc(documentOption.documentType, documentOption.subType)
                          : ''
                      }
                    >
                      {documentOption.available ? (
                        <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
                          <Icon icon={documentOption.icon} width="20" />
                        </div>
                      ) : (
                        <div className="flex justify-between">
                          <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
                            <Icon icon="lucide:file-lock" width="20" />
                          </div>
                          <Chip color="secondary">Coming soon</Chip>
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-semibold">{documentOption.title}</h3>
                        <p className="text-sm text-default-500">{documentOption.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <Spinner size="lg" />
              <span className="text-xl font-medium text-default-400">Your document is being generated</span>
              <span className="text-xl font-medium text-default-400">
                You will receive an email notification once it&apos;s ready
              </span>
            </>
          )}
        </div>
      )}

      <Drawer
        isOpen={drawerConfig.isOpen}
        isKeyboardDismissDisabled={false}
        size={'full'}
        onClose={() => setDrawerConfig({ isOpen: false })}
      >
        <DrawerContent>
          {(onClose) => (
            <>
              <DrawerHeader className="flex flex-col gap-1">Edit - {drawerConfig.variant}</DrawerHeader>
              <DrawerBody className="items-center">
                <TipTapEditor
                  key={drawerConfig.variant}
                  currentUserWithPermission={{
                    canEditTheWholeDocument: true,
                    sections: [],
                  }}
                  markdown={
                    drawerConfig?.variant === 'CUSTOM'
                      ? tender?.powerdoc?.customDocs?.[drawerConfig.index]?.content
                      : tender?.powerdoc?.[drawerConfig.variant]?.content
                  }
                  documentUUID={
                    drawerConfig?.variant === 'CUSTOM'
                      ? tender?.powerdoc?.customDocs?.[drawerConfig.index]?.uuid
                      : tender?.powerdoc?.uuid
                  }
                  isReadOnly={false}
                  doc={
                    drawerConfig?.variant === 'CUSTOM'
                      ? tender?.powerdoc?.customDocs?.[drawerConfig.index]
                      : tender?.powerdoc
                  }
                  docDesignSettings={docDesignSettings}
                  variant={drawerConfig.variant}
                  setDoc={console.log}
                  loadDocument={console.log}
                />
              </DrawerBody>
              <DrawerFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Close
                </Button>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  )
}
